import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "./Layout";
import SearchBar from "./SearchBar";
import ErrorBoundary from './ErrorBoundary';
import DynamicBreadcrumb from "./DynamicBreadcrumb";
import { useSchemaMicrodata } from '../hooks/useSchemaMicrodata';

export default function PageLayout({
  children,
  showSearch = true,
  title = "",
  titleHighlight = "",
  subtitle = "",
  className = "",
  initialQuery = "",
  initialType = "all",
  pageTitle = "",
  showGradient = false,
  searchBarClassName = "",
  showLogo = false, 
  logo = null,
  schemaData = null, // New prop for schema data
  headerActions = null,
  titleHighlightClassName = "",
  gradientClassName = ""
}) {
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get("q") || "";

  const searchQuery = initialQuery || queryParam || "";
  const searchType = initialType || searchParams.get("type") || "all";

  // Add schema microdata if provided
  useSchemaMicrodata(schemaData?.type, schemaData?.data, [schemaData]);

  useEffect(() => {
    // This effect ensures the SearchBar component updates when URL parameters change
    // It doesn't need to do anything, just depend on searchQuery and searchType
  }, [searchQuery, searchType]);

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow">
        <Layout pageTitle={pageTitle}>
          <div className="relative flex flex-col">
            {/* Background gradient - simplified for performance */}
            {showGradient &&
              <div className="w-full h-[400px] fixed top-0 -z-[1] pointer-events-none">
                <div className={`absolute inset-0 bg-gradient-to-b to-transparent ${gradientClassName || 'from-primary/[0.03]'}`} />
              </div>
            }

            <div className="container flex-grow px-6 mx-auto mt-4 max-md:mt-6">
              <DynamicBreadcrumb />

              <div className={`flex items-center justify-between gap-4 mb-4 ${showSearch ? 'max-md:flex-col-reverse' : 'max-md:flex-col'}`}>
                {/* Page title section */}
                {(title || subtitle) && (
                  <div className="max-md:text-center">
                    {title && (
                      <h1 className="font-medium text-26 max-md:text-20 text-secondary">
                        {title} <span className={`inline-block text-transparent bg-clip-text ${titleHighlightClassName || 'bg-gradient-title'}`}>{titleHighlight}</span>
                      </h1>
                    )}
                    {subtitle && (
                      <p className="mt-1 text-14 text-secondary/60">
                        {subtitle}
                      </p>
                    )}
                  </div>
                )}

                {/* Search bar */}
                {showSearch && (
                  <div className={`max-md:w-full md:min-w-[400px] ${searchBarClassName}`}>
                    <ErrorBoundary>
                      <SearchBar
                        key={`search-${searchQuery}-${searchType}`}
                        initialQuery={searchQuery}
                        initialType={searchType}
                      />
                    </ErrorBoundary>
                  </div>
                )}
                {showLogo && logo && (
                  <div className="flex items-center gap-2 my-auto">
                    <img src={logo}
                      alt="Logo"
                      className="object-contain w-full p-4 transition-transform duration-300 group-hover:scale-105 max-w-[200px] max-h-[100px]"
                      onError={(e) => {
                        e.target.src = "https://imageplaceholder.net/300x150/f3f4f6/131313?text=Logo";
                      }}
                      />
                  </div>
                )}

                {/* Header actions */}
                {!showSearch && headerActions && (
                  <div className="flex items-center gap-2 max-md:w-full max-md:justify-center">
                    {headerActions}
                  </div>
                )}
              </div>

              <div className={`flex-grow ${className}`}>
                {children}
              </div>
            </div>
          </div>
        </Layout>
      </main>
    </div>
  );
} 